/*
 * pdp-nav.js
 * ---------------------------------------------------------------
 * The mobile navigation drawer.
 *
 * Written rather than inherited. Dawn's MenuDrawer carries two
 * hard-coded delays -- a 100ms setTimeout before it will reveal a
 * submenu, and a 400ms requestAnimationFrame counter before it will
 * remove `open` -- neither of which is configurable, and it drives the
 * whole site's navigation so it is the wrong thing to edit for one
 * drawer.
 *
 * What this does that a `<details>` toggle does not:
 *   - moves focus into the panel on open and returns it to the button
 *   - traps Tab inside the panel while it is open
 *   - closes on Escape, from anywhere
 *   - locks the page behind without losing the scroll position
 *   - closes itself if the viewport grows past the desktop breakpoint
 *   - keeps aria-expanded on the button and the sub-buttons truthful
 * ---------------------------------------------------------------
 */
class NavDrawer extends HTMLElement {
  constructor() {
    super();
    this.toggle = this.querySelector('[data-ndrawer-toggle]');
    this.panel = this.querySelector('[data-ndrawer-panel]');
    this.scroller = null; // resolved from the panel once it is moved
    this.root = document.documentElement;
    this.subs = Array.from(this.querySelectorAll('[data-ndrawer-sub]'));
    this.open = false;

    this.onKeydown = this.onKeydown.bind(this);
    this.onResize = this.onResize.bind(this);
    this.measure = this.measure.bind(this);
  }

  connectedCallback() {
    if (!this.toggle || !this.panel) return;

    /* The panel is moved to <body>.
     *
     * Inside the header it was at the mercy of the header's own styles:
     * a `transform` or a `backdrop-filter` on any ancestor makes that
     * ancestor the containing block for a `position: fixed` child, so
     * the panel would have measured itself against a 59px-tall header.
     * The sticky header carries both.
     *
     * The workaround was to strip them while the menu was open -- which
     * is what made the header visibly snap from translucent-and-blurred
     * to solid white at the moment of the tap, before the menu had even
     * started to move. Two motions instead of one.
     *
     * With the panel on <body> the header is left completely alone: it
     * keeps its blur, its translucency and its transform, nothing about
     * it changes on open, and the only thing that moves is the menu. */
    /* The scrim goes to <body> first, so it lands behind the panel. */
    this.scrim = this.querySelector('[data-ndrawer-scrim]') ||
      document.body.querySelector(':scope > [data-ndrawer-scrim]');

    if (this.scrim && this.scrim.parentElement !== document.body) {
      const staleScrim = document.body.querySelector(':scope > [data-ndrawer-scrim]');
      if (staleScrim && staleScrim !== this.scrim) staleScrim.remove();
      document.body.appendChild(this.scrim);
    }

    if (this.panel.parentElement !== document.body) {
      const stale = document.body.querySelector(':scope > [data-ndrawer-panel]');
      if (stale && stale !== this.panel) stale.remove();
      document.body.appendChild(this.panel);
    }

    if (this.scrim) this.scrim.addEventListener('click', () => this.setOpen(false));

    /* Everything inside the panel is queried from the panel, not from
       `this` -- the panel is no longer a descendant of this element, so
       `this.querySelectorAll` would find none of it. */
    this.subs = Array.from(this.panel.querySelectorAll('[data-ndrawer-sub]'));
    this.scroller = this.panel.querySelector('.ndrawer__scroll');

    /* The panel's own close, now that it can cover the header. */
    const shut = this.panel.querySelector('[data-ndrawer-close]');
    if (shut) shut.addEventListener('click', () => this.setOpen(false, true));

    this.toggle.addEventListener('click', () => this.setOpen(!this.open));

    this.panel.querySelectorAll('[data-ndrawer-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openSub(btn));
    });

    this.panel.querySelectorAll('[data-ndrawer-close-sub]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeSub(btn.closest('[data-ndrawer-sub]')));
    });

    /* A tap on a link is a navigation, so the drawer should not still be
       sitting there when the next page paints from cache. */
    this.panel.addEventListener('click', (event) => {
      /* A tap on a link is a navigation. Animating the panel shut first
         means watching the menu fold away before the page changes, and
         both levels animating at once. It is dismissed outright: the
         class kills the transition, so it is simply gone. */
      if (event.target.closest('a[href]')) {
        this.panel.classList.add('is-navigating');
        this.setOpen(false);
      }
    });

    /* The country selector would not close on a second tap.
     *
     * Dawn binds `openSelector` to the button's click and `hidePanel` to
     * the form's focusout. Tapping an already-open selector fires
     * focusout first -- which hides it -- and then the click toggles it
     * straight back open. It looks like the button does nothing.
     *
     * Caught on the capture phase, before Dawn's handler: if it is open,
     * close it and stop the event there. */
    this.panel.addEventListener(
      'click',
      (event) => {
        const btn = event.target.closest('.localization-form__select');
        if (!btn || btn.getAttribute('aria-expanded') !== 'true') return;
        const panel = btn.parentElement.querySelector('.disclosure__list-wrapper');
        if (panel) panel.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
        event.stopPropagation();
        event.preventDefault();
      },
      true
    );

    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.measure);
    /* The toolbar slides in and out without firing `resize`. */
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.measure);
      window.visualViewport.addEventListener('scroll', this.measure);
    }
    this.measure();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('orientationchange', this.measure);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.measure);
      window.visualViewport.removeEventListener('scroll', this.measure);
    }
    this.unlock();
  }

  /* How tall the panel may be, measured rather than assumed.
   *
   * Dawn publishes `--viewport-height` and `--header-bottom-position`,
   * but it sets the first one inside `MenuDrawer.onSummaryClick` -- the
   * class this drawer replaced -- so on a fresh page load it is never
   * written at all. The CSS then fell back to `100vh`, and on iOS that
   * is the *large* viewport, measured with the URL bar hidden. The panel
   * ran past the bottom of the screen, the scroller believed its content
   * fitted, and the last few products could not be reached.
   *
   * `window.innerHeight` is the visible height right now, whatever the
   * browser chrome is doing. The header's own bottom edge gives the top.
   */
  measure() {
    /* Where the menu starts, and whether the header needs help getting
       there.

       With the sticky header working, the header is normally already in
       place when the menu opens, so nothing moves and there is no step.
       It only needs pinning in the one case Dawn hides it -- scrolled
       down under `on-scroll-up` -- and then the announcement bar is
       pinned with it, so the two arrive as the pair they are instead of
       the header alone covering the bar.

       This must run BEFORE the scroll lock. Locking makes the root a
       scroll container, which drops the sticky header back to its
       document position -- so measuring afterwards always saw it as
       off-screen and pinned it needlessly. */
    const header = document.querySelector('.header-wrapper');
    if (!header) return;
    const bar = document.querySelector('.anbar-section');

    const hr = header.getBoundingClientRect();
    const needsPin = this.open && hr.top < -1;
    document.documentElement.classList.toggle('ndrawer-pin', needsPin);

    const barH = needsPin && bar ? Math.round(bar.offsetHeight) : 0;
    this.root.style.setProperty('--ndrawer-bar', `${barH}px`);

    const top = needsPin
      ? barH + Math.round(header.offsetHeight)
      : Math.max(0, Math.round(hr.bottom));
    this.root.style.setProperty('--ndrawer-top', `${top}px`);

    /* iOS puts `position: fixed; bottom: 0` at the bottom of the layout
       viewport, which sits behind the Safari toolbar rather than above
       it. `visualViewport` is the only thing that reports what is
       actually visible. Elsewhere the two agree and this is 0. */
    const vv = window.visualViewport;
    const inset = vv ? Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)) : 0;
    this.root.style.setProperty('--ndrawer-bottom', `${inset}px`);

    /* Where the visible part of the screen starts.
     *
     * `--ndrawer-top` is the bottom of the header, which is what made the
     * menu read as something hanging off it. A floating panel starts from
     * the top of what can be seen, the way the search and cart panels
     * already do -- and on a phone that is not 0, because the layout
     * viewport scrolls behind the visual one. */
    this.root.style.setProperty(
      '--ndrawer-vtop',
      `${vv ? Math.max(0, Math.round(vv.offsetTop)) : 0}px`
    );
  }


  setOpen(next, options) {
    const opts = options || {};
    if (next === this.open) return;
    this.open = next;

    this.toggle.setAttribute('aria-expanded', String(next));

    /* The root class is what squares the header and takes its shadow
       off. Added immediately on open; on close it has to outlive the
       panel's collapse, or the shadow comes back and paints across a
       panel that is still on screen. */
    clearTimeout(this.rootTimer);
    if (next) {
      /* Order matters. Pin the header, measure the settled layout, then
         reveal on the next frame -- so the fade starts from a position
         that is already final rather than racing the reflow. */
      document.documentElement.classList.add('ndrawer-open');
      if (this.scrim) this.scrim.hidden = false;
      requestAnimationFrame(() => {
        if (this.open) this.panel.classList.add('is-open');
        if (this.open && this.scrim) this.scrim.classList.add('is-open');
      });
    } else {
      this.panel.classList.remove('is-open');
      this.panel.classList.remove('is-navigating');
      if (this.scrim) {
        this.scrim.classList.remove('is-open');
        /* Hidden only once it has faded, or it disappears in one frame
           while the panel is still travelling. */
        window.setTimeout(() => {
          if (!this.open && this.scrim) this.scrim.hidden = true;
        }, 300);
      }
      this.rootTimer = setTimeout(() => {
        document.documentElement.classList.remove('ndrawer-open');
        document.documentElement.classList.remove('ndrawer-pin');
      }, 300);
    }

    if (next) {
      /* Reset to level 1 on the way in, not on the way out.
         Resetting on close meant that tapping a product collapsed the
         sub-panel back to the category list first and then navigated --
         you saw the menu jump back a level before the page changed.
         Nothing is visible yet at this point, so doing it here costs
         nothing and is never seen. */
      this.subs.forEach((sub) => this.closeSub(sub, { silent: true }));
      /* Measured before locking -- see the note in measure(). */
      this.measure();
      this.lock();
      document.addEventListener('keydown', this.onKeydown);
      /* Focus the panel itself, not the first row.
         Focusing a row put a focus ring on it, and a ring that was
         triggered programmatically survives the round trip to a product
         page and back -- so reopening the menu showed a box around an
         item nobody had selected. The panel carries `tabindex="-1"` and
         no focus style, which is the standard way to hand focus to a
         dialog: the trap and Escape still work, and nothing is
         highlighted until someone actually tabs. */
      requestAnimationFrame(() => this.panel.focus({ preventScroll: true }));
    } else {
      this.unlock();
      document.removeEventListener('keydown', this.onKeydown);

      /* Focus only goes back to the burger when the keyboard closed the
         menu. Doing it after a tap put a focus ring on the button --
         programmatic focus counts as `:focus-visible` -- which is the
         box that kept appearing around it. A tap has already focused the
         button anyway, so there is nothing to restore. */
      if (opts.restoreFocus === true) this.toggle.focus();
    }
  }

  openSub(btn) {
    const sub = this.panel.querySelector('#' + btn.getAttribute('data-ndrawer-open'));
    if (!sub) return;
    /* No `hidden` juggling and no waiting a frame: the panel is shown by
       one class, and the level-1 list is hidden by one class on the root.
       The slide is a keyframe animation, which runs on its own from the
       moment the element appears -- a transition would need two painted
       states and that is what the frame-delay dance was working around. */
    this.subs.forEach((other) => {
      if (other !== sub) this.closeSub(other, { silent: true });
    });

    sub.classList.add('is-open');
    this.panel.classList.add('is-sub');
    btn.setAttribute('aria-expanded', 'true');
    this.scroller.scrollTop = 0;

    /* Focus the panel, never the back button. Programmatic focus counts
       as `:focus-visible`, so focusing a control drew a ring around it --
       the box that appeared on the back arrow and on the row you had just
       tapped. A container with `tabindex="-1"` and no focus style takes
       focus without showing anything. */
    sub.focus({ preventScroll: true });
  }

  closeSub(sub, options) {
    if (!sub) return;
    const silent = options && options.silent;
    sub.classList.remove('is-open');

    const opener = this.panel.querySelector('[data-ndrawer-open="' + sub.id + '"]');
    if (opener) opener.setAttribute('aria-expanded', 'false');
    if (!silent) this.panel.focus({ preventScroll: true });

    /* The root only goes back to level 1 once nothing is open, so
       closing one panel while another is opening cannot strand it. */
    if (!this.subs.some((s) => s.classList.contains('is-open'))) {
      this.panel.classList.remove('is-sub');
    }
  }

  focusables() {
    return Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      /* Escape inside a sub-panel steps back one level rather than
         throwing the whole menu away. */
      const openSub = this.subs.find((s) => s.classList.contains('is-open'));
      if (openSub) {
        this.closeSub(openSub);
      } else {
        this.setOpen(false, { restoreFocus: true });
      }
      return;
    }

    if (event.key !== 'Tab') return;

    const items = this.focusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onResize() {
    this.measure();
    if (this.open && window.matchMedia('(min-width: 990px)').matches) {
      this.setOpen(false);
    }
  }

  /* The page behind is locked with `overflow`, not by fixing the body.
     Fixing the body would take the sticky header with it: at 500px down
     the page, `top: -500px` puts the header off the top of the screen --
     and the drawer hangs off the header, so it would go with it.
     `overflow: hidden` freezes the page where it stands and leaves the
     header exactly where it is. */
  lock() {
    /* Locked on the root element only. `overflow: hidden` on `body`
       makes body a scroll container, which stops `position: sticky`
       working on the header -- the same fault Klaviyo's popup causes.
       The root propagates its overflow to the viewport, so the page is
       just as locked and nothing between the header and the viewport
       becomes a scroller. */
    this.prevHtmlOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  unlock() {
    if (this.prevHtmlOverflow === undefined) return;
    document.documentElement.style.overflow = this.prevHtmlOverflow;
    this.prevHtmlOverflow = undefined;
  }
}

if (!customElements.get('nav-drawer')) {
  customElements.define('nav-drawer', NavDrawer);
}
